import { Link, useParams } from "react-router-dom";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";
import RelatedMovies from "@/pages/detail/components/RelatedMovies";
import FavoriteButton from "@/components/feature/FavoriteButton";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { formatDuration, formatViews, getHotMovies, getMovieById, getRelatedMovies } from "@/lib/movies";
import { useSeo } from "@/hooks/useSeo";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  useSeo({
    title: movie ? `${movie.title} - 在线观看 - 免费看大片` : "影片未找到 - 免费看大片",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）${movie.genres.join("、")}电影，由${movie.director}执导，${movie.cast.slice(0, 3).join("、")}主演。${movie.synopsis.slice(0, 60)}免费看大片提供影片详情、评分与同类推荐。`
      : "该影片不存在或已下架，返回首页继续浏览最新热门电影。",
    keywords: movie
      ? `${movie.title},${movie.title}在线观看,${movie.genres.join(",")},免费看大片`
      : "免费看大片,电影在线观看",
  });

  if (!movie) {
    const hotMovies = getHotMovies(5);
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-400">
            <i className="ri-film-line text-2xl" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-50">
            没有找到这部影片
          </h1>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
            影片可能已下架或链接有误。你可以回到首页，或从下面的热门影片中挑一部。
          </p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line text-base" />
            返回首页
          </Link>
        </div>

        <section className="mt-12">
          <SectionHeading eyebrow="Popular now" title="热门影片推荐" />
          <MovieGrid movies={hotMovies} />
        </section>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 5);
  const primaryGenre = movie.genres[0] || "全部";

  return (
    <>
      <section className="relative overflow-hidden border-b border-background-200/60">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop || movie.poster}
            alt={`${movie.title}剧照背景`}
            className="h-full w-full object-cover object-center opacity-30 blur-[1px]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-background-50/60" />
        </div>

        <div className="relative mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
          <nav
            aria-label="面包屑导航"
            className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500"
          >
            <Link to="/" className="transition-colors hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <Link
              to={`/category/${encodeURIComponent(primaryGenre)}`}
              className="transition-colors hover:text-primary-300"
            >
              {primaryGenre}片
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <span className="text-foreground-200">{movie.title}</span>
          </nav>

          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0">
              <p className="font-heading text-[11px] uppercase tracking-[0.32em] text-foreground-500">
                {movie.enTitle}
              </p>
              <h1 className="mt-2 font-heading text-3xl font-semibold leading-tight text-foreground-50 md:text-5xl">
                {movie.title}
              </h1>
              <p className="mt-2 text-sm text-foreground-300 md:text-base">{movie.tagline}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-foreground-400 md:text-sm">
                <span className="inline-flex items-center gap-1 rounded-md bg-accent-500/15 px-2 py-1 font-heading font-semibold text-accent-300">
                  <i className="ri-star-fill text-[11px]" />
                  {movie.rating.toFixed(1)}
                </span>
                <span>{movie.year}</span>
                <span className="text-foreground-700">·</span>
                <span>{movie.region}</span>
                <span className="text-foreground-700">·</span>
                <span>{formatDuration(movie.duration)}</span>
                <span className="text-foreground-700">·</span>
                <span>{formatViews(movie.views)}次播放</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${encodeURIComponent(genre)}`}
                    className="rounded-md border border-background-300/80 bg-background-100/70 px-2.5 py-1 text-xs text-foreground-300 backdrop-blur-sm transition-colors hover:border-primary-500/60 hover:text-primary-300"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("player")?.scrollIntoView({ behavior: "smooth", block: "center" })
                }
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg" />
                立即播放
              </button>
              <FavoriteButton movieId={movie.id} variant="full" />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-8">
          <div className="min-w-0">
            <PlayerDemo movie={movie} />

            <section className="mt-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-50 md:text-xl">
                剧情简介
              </h2>
              <div className="mt-3 h-px w-10 bg-accent-500" />
              <p className="mt-4 text-sm leading-7 text-foreground-300 md:text-[15px] md:leading-8">
                {movie.synopsis}
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-400 md:text-[15px] md:leading-8">
                影片由 {movie.director} 执导，{movie.cast.join("、")} 联袂出演，属于
                {movie.genres.join(" / ")}类型，{movie.year} 年在{movie.region}地区上映，片长
                {formatDuration(movie.duration)}。
              </p>
            </section>

            <section className="mt-8 rounded-lg border border-background-200/70 bg-background-100/60 p-4 md:p-5">
              <h2 className="font-heading text-sm font-semibold text-foreground-100">
                影片看点
              </h2>
              <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {[
                  `类型标签：${movie.genres.join(" / ")}`,
                  `导演：${movie.director}`,
                  `主演：${movie.cast.slice(0, 3).join("、")}`,
                  `上映年份：${movie.year}`,
                  `制作地区：${movie.region}`,
                  `片长：${formatDuration(movie.duration)}`,
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-foreground-400 md:text-sm">
                    <i className="ri-check-line mt-0.5 text-primary-300" />
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside>
            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <div className="relative aspect-[2/3] w-full">
                <img
                  src={movie.poster}
                  alt={`${movie.title}（${movie.year}）电影海报`}
                  title={`${movie.title} 在线观看 - 免费看大片`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent" />
                <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-background-950/80 px-2 py-1 font-heading text-sm font-semibold text-accent-400 backdrop-blur-sm">
                  <i className="ri-star-fill text-xs" />
                  {movie.rating.toFixed(1)}
                </span>
              </div>

              <div className="p-4 md:p-5">
                <dl className="flex flex-col gap-2.5 text-xs md:text-sm">
                  {[
                    { label: "导演", value: movie.director },
                    { label: "主演", value: movie.cast.join(" / ") },
                    { label: "类型", value: movie.genres.join(" / ") },
                    { label: "地区", value: movie.region },
                    { label: "年份", value: `${movie.year} 年` },
                    { label: "片长", value: formatDuration(movie.duration) },
                    { label: "播放量", value: `${formatViews(movie.views)}次` },
                  ].map((row) => (
                    <div key={row.label} className="flex gap-3">
                      <dt className="w-14 shrink-0 text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 flex-1 leading-relaxed text-foreground-200">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-4 flex flex-col gap-2">
                  <FavoriteButton movieId={movie.id} variant="full" className="w-full" />
                  <Link
                    to={`/category/${encodeURIComponent(primaryGenre)}`}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
                  >
                    <i className="ri-folder-open-line text-base" />
                    更多{primaryGenre}片
                  </Link>
                </div>

                <div className="mt-4 flex items-start gap-2 rounded-md bg-background-200/50 p-3">
                  <i className="ri-information-line mt-0.5 text-sm text-accent-400" />
                  <p className="text-xs leading-relaxed text-foreground-500">
                    播放区域为前端演示，暂不提供真实影片资源，收藏可保存在本机浏览器中。
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <RelatedMovies movies={related} genre={primaryGenre} />
      </div>
    </>
  );
}